/**
 * Builds the embed snippets shown in the booking link embed-code UI.
 *
 * Pure module: no React, no side effects at import. Callers resolve the tag
 * origin via `getTagOrigin()` and pass it in — `buildEmbedSnippet` never
 * reads `window`/config itself.
 */
import { getApiBaseUrl } from '@/lib/config';

export type EmbedMode = 'popup' | 'floating';

function isUsable(v: string | null | undefined): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

/** App/tag origin with protocol, no trailing slash. */
export function getTagOrigin(): string {
  const configured = getApiBaseUrl();
  const origin = configured || window.location.origin;
  return origin.replace(/\/+$/, '');
}

export function buildEmbedSnippet({
  mode,
  tagOrigin,
  slug,
  bookingLink,
}: {
  mode: EmbedMode;
  tagOrigin: string;
  slug: string;
  bookingLink: string;
}): string | null {
  if (!isUsable(slug) || !isUsable(bookingLink) || !isUsable(tagOrigin)) {
    return null;
  }

  const scriptTag = `<script src="${tagOrigin}/static/js/ahoy.js" data-webhook-slug="${slug}" async></script>`;

  switch (mode) {
    case 'popup':
      // href points at the real booking URL (not "#") so the link still
      // reaches booking if ahoy.js is blocked or slow to load; ahoy.js calls
      // e.preventDefault() on the delegated click, so the JS popup takes over
      // whenever the widget is available.
      return [
        '<!-- Ahoy booking -->',
        scriptTag,
        `<a href="${bookingLink}" data-ahoy-book="${bookingLink}">Book a meeting</a>`,
      ].join('\n');
    case 'floating':
      // Declarative init via data-ahoy-book-floating: ahoy.js mounts the
      // floating button from its own bootstrap once the booking API exists,
      // avoiding the race where an inline DOMContentLoaded handler could run
      // before the async ahoy.js has defined window.ahoy.book.
      return [
        '<!-- Ahoy booking (floating button) -->',
        `<script src="${tagOrigin}/static/js/ahoy.js" data-webhook-slug="${slug}" data-ahoy-book-floating="${bookingLink}" async></script>`,
      ].join('\n');
  }
}
